import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { selectRecurrences } from '@/features/domain/routeplan'
import { useNotification } from '@/hooks'
import { type ControlledNavigateFunction } from '@/routing'
import { isDeepEqual } from '@/server-data'
import { useAppDispatch } from '@/store'

import * as OrderTexts from '../../../../intl'
import { checkCanAddOrders } from './checkCanAddOrders'
import { computeAutomaticRange } from './computeAutomaticRange'
import { computeBulkOrderChanges } from './computeBulkOrderChanges'
import { computeDirtyFields } from './computeDirtyFields'
import { computeRecurrenceUpdateRequest } from './computeRecurrenceUpdateRequest'

import { FormData } from '../../types'

type UseOnSubmitArgs = {
  updateOrders: (data: uui.domain.ui.forms.BulkOrderUpdate) => Promise<boolean>
  clearEditingState: () => void
  orders: uui.domain.client.rm.ExtendedOrderStep[]
  setAllowedTargetRanges: (targetRanges: uui.domain.ui.forms.RecurrenceCollectedTargetRange) => void
  collectTargetRange: () => Promise<uui.domain.ui.forms.TargetRange>
  confirmFromRecurrenceToSingleOrder: () => Promise<void>
  alertUserUnableToCreateRecurrenceOrder: () => Promise<void>
  navigate: ControlledNavigateFunction<uui.routing.Setup>
  initialValues: FormData
}

export function useOnSubmit({
  updateOrders,
  clearEditingState,
  orders,
  collectTargetRange,
  alertUserUnableToCreateRecurrenceOrder,
  confirmFromRecurrenceToSingleOrder,
  setAllowedTargetRanges,
  navigate,
  initialValues,
}: UseOnSubmitArgs) {
  const dispatch = useAppDispatch()
  const toast = useNotification()
  const recurrences = useSelector(selectRecurrences)
  const [submitting, setSubmitting] = useState<boolean>(false)

  const onSubmit = useCallback(
    async (currentValues: FormData) => {
      try {
        setSubmitting(true)

        if (isDeepEqual(initialValues, currentValues)) {
          setSubmitting(false)
          clearEditingState()
          return
        }

        const { dirtyFields } = computeDirtyFields(initialValues, currentValues)

        const allBulkOrderChanges = computeBulkOrderChanges(currentValues, orders)

        const orderChangesUnchanged = isDeepEqual(
          computeBulkOrderChanges(initialValues, orders),
          allBulkOrderChanges,
        )
        const recurrenceUnchanged = isDeepEqual(
          initialValues.recurrenceSettings,
          currentValues.recurrenceSettings,
        )
        if (orderChangesUnchanged && recurrenceUnchanged) {
          setSubmitting(false)
          clearEditingState()
          return
        }

        async function alertUserNotAbleToCreateOrders() {
          await alertUserUnableToCreateRecurrenceOrder()
          navigate('territory')
        }

        const wasNotRecurrentOrders = orders.every(o => !o.order.recurrenceId)
        const isNotRecurrentOrders =
          currentValues.recurrenceSettings.status === 'exact' &&
          currentValues.recurrenceSettings.value.recurrencePeriod.recurrenceType === 'NO_REPEAT'

        const neverBeenRecurrentOrders = wasNotRecurrentOrders && isNotRecurrentOrders

        const bulkOrderChanges = filterBulkOrderChangesWithOnlyDirtyFields(
          allBulkOrderChanges,
          dirtyFields,
        )

        if (neverBeenRecurrentOrders) {
          const bulkOrderUpdate = {
            type: 'bulk' as const,
            orderChanges: bulkOrderChanges,
          }
          const canAddOrders = await checkCanAddOrders(bulkOrderUpdate, dispatch)

          if (!canAddOrders) {
            await alertUserNotAbleToCreateOrders()
            return
          }

          const result = await updateOrders(bulkOrderUpdate)

          // Clear FormState submitting mode
          setSubmitting(false)

          // if the update has been completed successfully let's close the form
          if (result) clearEditingState()
          return
        }

        // Recurrent orders handling
        const automaticRange = computeAutomaticRange({
          initialValues,
          currentValues,
        })

        if (automaticRange === 'NOT_REPEAT') {
          await confirmFromRecurrenceToSingleOrder()
        }

        const { recurrenceSettings: initialRecurrenceSettings, ...initialOrderData } = initialValues
        const { recurrenceSettings: currentRecurrenceSettings, ...currentOrderData } = currentValues

        const eligibilityUpdated =
          currentRecurrenceSettings.status !== 'mixed' &&
          initialRecurrenceSettings.status !== 'mixed' &&
          currentRecurrenceSettings.value.recurrencePeriod.date !==
            initialRecurrenceSettings.value.recurrencePeriod.date

        const recurrenceUpdated =
          (initialRecurrenceSettings.status === 'mixed' &&
            currentRecurrenceSettings.status !== 'mixed') ||
          (initialRecurrenceSettings.status !== 'mixed' &&
            currentRecurrenceSettings.status !== 'mixed' &&
            !isDeepEqual(
              computeRecurrenceUpdateRequest(currentRecurrenceSettings.value),
              computeRecurrenceUpdateRequest(initialRecurrenceSettings.value),
            ))

        const orderUpdated = !isDeepEqual(initialOrderData, currentOrderData)

        const lastOrderOfRecurrence = !orders.some(o => o.order.nextRecurrentOrder)
        const moreOrdersWillBeGenerated = orders.some(o => {
          const recurrence = o.order.recurrenceId && recurrences[o.order.recurrenceId]
          return recurrence && recurrence.nextRecurrentOrderDate != null
        })

        const newTargetRanges = getNewTargetRanges(
          eligibilityUpdated,
          recurrenceUpdated,
          orderUpdated,
          lastOrderOfRecurrence,
          moreOrdersWillBeGenerated,
          OrderTexts,
        )

        setAllowedTargetRanges(newTargetRanges)

        const collectedTargetRange = automaticRange ?? (await collectTargetRange())

        if (currentValues.recurrenceSettings.status === 'mixed') {
          const update = {
            type: 'bulk-recurrence-unchanged',
            orderChanges: bulkOrderChanges,
            targetRange: collectedTargetRange,
          } as const

          // since we are just updating the orders without changing the recurrence settings
          // we don't need to check if we can add orders
          const result = await updateOrders(update)

          // Clear FormState submitting mode
          setSubmitting(false)

          // if the update has been completed successfully let's close the form
          if (result) clearEditingState()
          return
        }

        const dateChanged = hasDateChanged(initialValues, currentValues)

        const isNotNoRepeat =
          currentValues.recurrenceSettings.value.recurrencePeriod.recurrenceType !== 'NO_REPEAT'

        if (dateChanged && collectedTargetRange !== 'NOT_REPEAT' && isNotNoRepeat) {
          if (!bulkOrderChanges.orderData) {
            bulkOrderChanges.orderData = {}
          }

          bulkOrderChanges.orderData.eligibility = {
            type: 'on',
            dates: [currentValues.recurrenceSettings.value.recurrencePeriod.date],
          }
        }

        const bulkRecurrenceUpdate = {
          type: 'bulk-recurrence' as const,
          orderChanges: bulkOrderChanges,
          updateRecurrence: computeRecurrenceUpdateRequest(currentValues.recurrenceSettings.value),
          targetRange: collectedTargetRange,
          startDate: currentValues.recurrenceSettings.value.recurrencePeriod.date,
        } as const

        const canAddOrders = await checkCanAddOrders(bulkRecurrenceUpdate, dispatch)

        if (!canAddOrders) {
          alertUserNotAbleToCreateOrders()
          return
        }

        const result = await updateOrders(bulkRecurrenceUpdate)

        // Clear FormState submitting mode
        setSubmitting(false)

        // if the update has been completed successfully let's close the form
        if (result) clearEditingState()
      } catch (e) {
        if (e instanceof Error) {
          toast.error(e.message)
        }
        setSubmitting(false)
      }
    },
    [updateOrders, clearEditingState, orders, dispatch],
  )

  return {
    onSubmit,
    submitting,
  }
}

function hasDateChanged(initialValues: FormData, currentValues: FormData): boolean {
  if (initialValues.recurrenceSettings.status !== currentValues.recurrenceSettings.status) {
    return true
  }

  if (initialValues.recurrenceSettings.status === 'mixed') {
    return false
  }

  if (currentValues.recurrenceSettings.status === 'mixed') {
    return false
  }

  return (
    initialValues.recurrenceSettings.value.recurrencePeriod.date !==
    currentValues.recurrenceSettings.value.recurrencePeriod.date
  )
}

/**
 * Remove from the bulkOrderChanges object all fields that are not dirty
 * @param bulkOrderChanges all fields
 * @param dirtyFields object with a boolean for each field that is dirty
 * @returns a bulkOrderChanges object with only dirty fields
 */
function filterBulkOrderChangesWithOnlyDirtyFields(
  bulkOrderChanges: uui.domain.ui.forms.BulkOrderChanges,
  dirtyFields: Partial<Record<keyof FormData, boolean>>,
): uui.domain.ui.forms.BulkOrderChanges {
  const filteredChanges = { ...bulkOrderChanges }
  //Added loadsDelta to dirtyFields as loads is defined as loadsDelta in uui.domain.ui.forms.BulkOrderChanges
  const modifiedDirtyFields = { ...dirtyFields, loadsDelta: dirtyFields.loads }

  for (const key in filteredChanges.orderData) {
    if (!modifiedDirtyFields[key as keyof FormData]) {
      delete filteredChanges.orderData[key]
    }
  }
  for (const key in filteredChanges.orderStepData) {
    if (!modifiedDirtyFields[key as keyof FormData]) {
      delete filteredChanges.orderStepData[key]
    }
  }

  return filteredChanges
}

function getNewTargetRanges(
  eligibilityUpdated: boolean,
  recurrenceUpdated: boolean,
  orderUpdated: boolean,
  lastOrderOfRecurrence: boolean,
  moreOrdersWillBeGenerated: boolean,
  OrderTexts: {
    getFutureOptionDisabledTooltip: () => string
    getAllOptionDisabledTooltip: () => string
    getFutureOptionDisabledForLastOccurrenceTooltip: () => string
    getOrderOptionHelperText: () => string
    getFutureOptionHelperText: () => string
    getAllOptionHelperText: () => string
  },
): uui.domain.ui.forms.RecurrenceCollectedTargetRange {
  if (eligibilityUpdated && !recurrenceUpdated && !orderUpdated) {
    return {
      ORDER: { visible: true, disabled: false },
      FUTURE: {
        visible: true,
        disabled: true,
        tooltip: OrderTexts.getFutureOptionDisabledTooltip(),
      },
      ALL: { visible: true, disabled: true, tooltip: OrderTexts.getAllOptionDisabledTooltip() },
    }
  }

  if (recurrenceUpdated && eligibilityUpdated) {
    return {
      ORDER: { visible: true, disabled: false, helperText: OrderTexts.getOrderOptionHelperText() },
      FUTURE: {
        visible: true,
        disabled: false,
        helperText: OrderTexts.getFutureOptionHelperText(),
      },
      ALL: { visible: true, disabled: false, helperText: OrderTexts.getAllOptionHelperText() },
    }
  }

  if (recurrenceUpdated && !orderUpdated) {
    return {
      ORDER: { visible: false, disabled: true },
      FUTURE: { visible: true, disabled: false },
      ALL: { visible: true, disabled: false },
    }
  }

  if (
    !eligibilityUpdated &&
    !recurrenceUpdated &&
    orderUpdated &&
    lastOrderOfRecurrence &&
    !moreOrdersWillBeGenerated
  ) {
    return {
      ORDER: { visible: true, disabled: false },
      FUTURE: {
        visible: true,
        disabled: true,
        tooltip: OrderTexts.getFutureOptionDisabledForLastOccurrenceTooltip(),
      },
      ALL: { visible: true, disabled: false },
    }
  }

  return {
    ORDER: {
      visible: true,
      disabled: false,
      helperText: recurrenceUpdated ? OrderTexts.getOrderOptionHelperText() : undefined,
    },
    FUTURE: { visible: true, disabled: false },
    ALL: { visible: true, disabled: false },
  }
}
